'use client'

import type { C2CClientTemplates } from '@/lib/c2c/copy-declaration'
import { useClientTemplate } from '@/lib/content-standard/client-template'
import type { Clock, Count, Duration, Money } from '@/lib/facts/values'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { CrawlSafeLink } from '@/components/crawl-safe-link'

import { DurationConnector } from './duration-connector'
import { BusIcon } from './icons/bus-icon'
import { TrainIcon } from './icons/train-icon'
import { StarRating } from './star-rating'

/**
 * The store keys this card's chrome renders whole. `transModeLabel` is the route's mode word
 * (the store's `bus`/`train`/… key) — never a word derived from the URL slug.
 */
type TripCardLabelKey = 'luxury' | 'transModeLabel' | 'stars'

interface TripCardProps {
    departTime: Clock
    arriveTime: Clock
    departStation: string
    arriveStation: string
    duration: Duration
    /** Absent when the trip cannot be stated in the page's currency. */
    fare?: Money
    carrierName: string
    carrierLogo?: string
    carrierRating?: number
    isLuxury?: boolean
    isNextGen?: boolean
    searchUrl: string
    transfers?: Count
    /** Route transport mode — picks the glyph only; the mode *word* comes from the store. */
    mode?: string
    /** The fare, the clocks, the duration and the transfer count, each a client template
     *  the page's decision formats. */
    templates: C2CClientTemplates
    /** Chrome copy, keyed by the translation-store key each carries. */
    labels?: Partial<Record<TripCardLabelKey, string>>
    /** Fired before navigation when the card is clicked. */
    onSelect?: () => void
}

const tooltipContentClass = 'rounded-none bg-[#2e3033] px-5 py-4 text-sm text-white'

export function TripCard({
    departTime,
    arriveTime,
    departStation,
    arriveStation,
    duration,
    fare,
    carrierName,
    carrierLogo,
    carrierRating,
    isLuxury = false,
    isNextGen = false,
    searchUrl,
    transfers,
    mode,
    templates,
    labels,
    onSelect,
}: TripCardProps) {
    const fareText = useClientTemplate(templates.tripCardFare)
    const clock = useClientTemplate(templates.tripCardClock)
    const durationText = useClientTemplate(templates.tripCardDuration)
    const transfersText = useClientTemplate(templates.tripCardTransfers)
    const departs = clock({ time: departTime }).text
    const arrives = clock({ time: arriveTime }).text
    const formattedDuration = durationText({ travelTime: duration }).text
    const transferBadge =
        transfers !== undefined && transfers > 0 ? transfersText({ transfers }).text : undefined
    const modeLabel = labels?.transModeLabel
    // The glyph tracks the route's transport mode — a bus icon on a train trip is a defect
    // readers notice immediately. Bus is the fallback for the modes with no dedicated icon
    // (ferry/flight).
    const ModeIcon = mode === 'train' ? TrainIcon : BusIcon
    const modeIconTestId = `trip-mode-icon-${mode === 'train' ? 'train' : 'bus'}`
    // Luxury is copy and comes from the store; NextGen is Amtrak's product name, which
    // reads the same in every locale.
    const badges = [
        isLuxury ? labels?.luxury : undefined,
        isNextGen ? 'NextGen' : undefined,
    ].filter((badge): badge is string => Boolean(badge))

    return (
        <CrawlSafeLink
            url={searchUrl}
            onSelect={onSelect}
            testId="trip-card"
            className="block w-full cursor-pointer rounded-[7px] border border-[#e2e2e2] bg-white p-4 text-left transition-shadow hover:shadow-md md:px-6 md:py-5"
        >
            <div className="mb-3 flex items-center gap-2 md:mb-4">
                <span className="md:hidden">
                    <ModeIcon
                        className="h-4 w-4 shrink-0 text-[#878787]"
                        data-testid={modeIconTestId}
                    />
                </span>
                {carrierLogo ? (
                    <>
                        <span className="truncate text-sm text-[#767676] uppercase md:hidden">
                            {carrierName}
                        </span>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src={carrierLogo}
                                    alt={carrierName}
                                    className="hidden h-5 max-w-[80px] object-contain md:block"
                                />
                            </TooltipTrigger>
                            <TooltipContent side="top" className={tooltipContentClass}>
                                {/* Without the store's mode word the tooltip names the carrier alone. */}
                                {modeLabel ? `${carrierName} (${modeLabel})` : carrierName}
                            </TooltipContent>
                        </Tooltip>
                    </>
                ) : (
                    <span className="truncate text-sm text-[#767676] uppercase">{carrierName}</span>
                )}
                {carrierRating != null && (
                    <StarRating rating={carrierRating} label={labels?.stars} className="md:ml-1" />
                )}
                {fare !== undefined && (
                    <span className="ml-auto shrink-0 text-lg font-bold text-[#007380] md:text-xl md:text-[#44474a]">
                        {fareText({ price: fare }).text}
                    </span>
                )}
            </div>

            <div className="flex items-start">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div className="mr-4 hidden h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-white bg-[#ebeef3] md:flex">
                            <ModeIcon
                                className="w-[26px] text-[#44474a]"
                                data-testid={modeIconTestId}
                            />
                        </div>
                    </TooltipTrigger>
                    {/* The mode word IS this tooltip, so it goes with the store value. */}
                    {modeLabel && (
                        <TooltipContent side="top" className={tooltipContentClass}>
                            {modeLabel}
                        </TooltipContent>
                    )}
                </Tooltip>

                <div className="flex min-w-0 flex-1 items-baseline gap-1 md:items-start md:gap-0">
                    <div className="shrink-0 md:min-w-0 md:flex-[2]">
                        <p className="text-lg font-bold text-[#2e3033] md:text-xl">{departs}</p>
                        <p className="hidden truncate text-sm leading-[21px] text-[#2e3033] md:block">
                            {departStation}
                        </p>
                    </div>

                    <span className="text-xs text-[#767676] italic md:hidden">
                        {formattedDuration}
                    </span>
                    <div className="hidden md:flex md:flex-1 md:items-center md:px-3">
                        <DurationConnector duration={formattedDuration} />
                    </div>

                    <div className="shrink-0 md:min-w-0 md:flex-[2]">
                        <p className="text-lg font-bold text-[#2e3033] md:text-xl">{arrives}</p>
                        <p className="hidden truncate text-sm leading-[21px] text-[#2e3033] md:block">
                            {arriveStation}
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-4 text-sm text-[#2e3033] md:hidden">
                <span className="truncate">{departStation}</span>
                <span className="truncate">{arriveStation}</span>
            </div>

            {transferBadge && (
                <p className="mt-2 text-xs text-[#767676] md:pl-[60px]">{transferBadge}</p>
            )}

            {/* Feature chips: exactly these two, sharing one style. Transfers stay a line of
                their own above rather than a chip. */}
            {badges.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                    {badges.map((badge) => (
                        <span
                            key={badge}
                            className="inline-block rounded-2xl bg-[#e2d3fa] px-3 text-sm text-[#6341b4]"
                        >
                            {badge}
                        </span>
                    ))}
                </div>
            )}
        </CrawlSafeLink>
    )
}
